// Mirrors wgpt/app/validators/wgpt/social_media_post_validator.rb and
// wgpt/app/models/wgpt_social_media_post.rb - keep both sides in sync when either changes.

// Applied regardless of destination platform - media_url_for re-encodes any image the
// destination platform's API would reject on publish, so what matters here is only "can we
// decode this", not what any individual platform's API happens to accept as input. Keep in sync
// with the Rails validator's IMAGE_CONTENT_TYPES.
export const IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/tiff',
];

export const VIDEO_CONTENT_TYPES = {
  facebook_post: ['video/mp4', 'video/quicktime'],
  facebook_reel: ['video/mp4', 'video/quicktime'],
  instagram_post: ['video/mp4', 'video/quicktime'],
  tiktok_post: ['video/mp4', 'video/quicktime', 'video/webm'],
};

export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 300 * 1024 * 1024;

export const VIDEO_DURATION_RANGE_SECONDS = {
  facebook_reel: { min: 3, max: 90 },
  instagram_post: { min: null, max: 90 },
  tiktok_post: { min: null, max: 300 },
};

// Each platform's documented max number of images in a single post - see MAX_IMAGE_COUNT in the
// Rails validator for sourcing.
export const MAX_IMAGE_COUNT = {
  facebook_post: 50,
  instagram_post: 10,
  tiktok_post: 35,
};

// Each platform's recommended feed image canvas, shown to the user as guidance only - on
// publish, images are posted as-is unless the platform's API would reject them (see
// Wgpt::SocialMediaPosts::PublishMedia in the backend).
export const IMAGE_DIMENSIONS = {
  facebook_post: [1080, 1350],
  instagram_post: [1080, 1350],
  tiktok_post: [1080, 1920],
};

// We don't resize/transcode video, so these are shown as guidance only, not enforced beyond the
// duration check above.
export const RECOMMENDED_VIDEO_DIMENSIONS = {
  facebook_reel: [1080, 1920],
  instagram_post: [1080, 1920],
  tiktok_post: [1080, 1920],
};

export const isImageFile = file => file.type.startsWith('image/');
export const isVideoFile = file => file.type.startsWith('video/');

export const bytesToMB = bytes => Math.round(bytes / (1024 * 1024));

export const validateFileTypeAndSize = (file, postType) => {
  if (isImageFile(file)) {
    if (!IMAGE_CONTENT_TYPES.includes(file.type)) {
      return {
        type: 'unsupported_image_type',
        meta: { contentType: file.type },
      };
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return {
        type: 'file_too_large',
        meta: { limitMB: bytesToMB(MAX_IMAGE_BYTES) },
      };
    }
    return null;
  }

  if (isVideoFile(file)) {
    const allowed = VIDEO_CONTENT_TYPES[postType] || [];
    if (!allowed.includes(file.type)) {
      return {
        type: 'unsupported_video_type',
        meta: { contentType: file.type },
      };
    }
    if (file.size > MAX_VIDEO_BYTES) {
      return {
        type: 'file_too_large',
        meta: { limitMB: bytesToMB(MAX_VIDEO_BYTES) },
      };
    }
    return null;
  }

  return { type: 'unsupported_file' };
};

// Reads duration by loading the file into an off-DOM <video> element - the only way to inspect
// video duration client-side without uploading first.
export const readVideoMetadata = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const { duration } = video;
      URL.revokeObjectURL(url);
      resolve({ duration });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read video metadata'));
    };
    video.src = url;
  });

export const validateVideoSpec = ({ duration }, postType) => {
  const range = VIDEO_DURATION_RANGE_SECONDS[postType];
  if (range && duration) {
    if (range.min && duration < range.min) {
      return { type: 'duration_too_short', meta: { min: range.min } };
    }
    if (range.max && duration > range.max) {
      return { type: 'duration_too_long', meta: { max: range.max } };
    }
  }

  return null;
};

export const validateMediaCombination = (files, postType) => {
  const videos = files.filter(isVideoFile);
  const images = files.filter(isImageFile);

  if (postType === 'facebook_reel') {
    return files.length === 1 && videos.length === 1
      ? null
      : { type: 'reel_requires_single_video' };
  }

  if (videos.length && images.length) {
    return { type: 'cannot_mix_image_and_video' };
  }
  if (videos.length > 1) {
    return { type: 'only_one_video' };
  }

  const maxImages = MAX_IMAGE_COUNT[postType];
  if (maxImages && images.length > maxImages) {
    return {
      type: 'too_many_images',
      meta: { max: maxImages, count: images.length },
    };
  }
  return null;
};

export const recommendedMediaHint = postType => {
  const imageDimensions = IMAGE_DIMENSIONS[postType];
  const videoDimensions = RECOMMENDED_VIDEO_DIMENSIONS[postType];
  const durationRange = VIDEO_DURATION_RANGE_SECONDS[postType];
  const maxImages = MAX_IMAGE_COUNT[postType];

  return {
    imageDimensions: imageDimensions ? imageDimensions.join('×') : null,
    videoDimensions: videoDimensions ? videoDimensions.join('×') : null,
    maxDurationSeconds: durationRange?.max || null,
    maxImages: maxImages || null,
  };
};
